import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, FormControlLabel, Grid, Radio, Skeleton, Typography } from "@mui/material";
import type {
  Metier,
  CreateContactMetierDto,
  ContactMetier,
  Territoire,
  ContactMetierQueryFilter,
  TypeContactMetier,
} from "models";
import { MetierNiveau } from "models";
import type Competence from "constants/Competence";
import { useSnackbarErrorHandler, withPageErrorBoundary } from "utils/errorHandling";
import type { SubmitHandler } from "react-hook-form";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { onFormErrorDefault } from "utils";
import {
  CompetenceSelect,
  ControlledCommuneAssocieeDelegueeAutoComplete,
  ControlledCommuneAutoComplete,
  ControlledMetierAutoComplete,
  ControlledPosteAutoComplete,
} from "components/inputs";
import { enqueueSnackbar } from "notistack";
import { ToastMessages } from "enums";
import { contactsMetiersService } from "services";
import { useMetiers, usePostes } from "hooks";
import { ControlledPerimetreAutoComplete } from "components/inputs/ControlledPerimetreAutoComplete";
import { ControlledTerritoireAutoComplete } from "components/inputs/ControlledTerritoireAutoComplete";
import { PageLayout } from "components/Layout";
import { SimilarCreationConfirmationDialog } from "./components/SimilarCreationConfirmationDialog";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";

/**
 * Retourne le `MetierNiveau` correspondant au `$type` d'un contact métier
 * @param type Type discriminant du contact métier
 * @returns La valeur de l'énum `MetierNiveau` si correspondance sinon `undefined`
 */
function getMetierNiveauFromTypeContactMetier(type?: TypeContactMetier): MetierNiveau | undefined {
  switch (type) {
    // Commune & Commune Associée Déléguée partage le même niveau
    case "commune":
    case "commune-associee-deleguee":
      return MetierNiveau.Commune;
    case "perimetre":
      return MetierNiveau.Perimetre;
    case "territoire":
      return MetierNiveau.Territoire;
    default:
      return undefined;
  }
}

const defaultValues: CreateContactMetierDto = {
  $type: "commune",
  metier: null,
  poste: null,
  commune: null,
  cad: null,
  perimetre: null,
  territoire: null,
};

function NouveauContactMetierPage(): ReactNode {
  const [competence, setCompetence] = useState<Competence>();
  const { metiers, isMetiersLoading } = useMetiers();
  const { postes, isPostesLoading } = usePostes();

  const formMethods = useForm<CreateContactMetierDto>({
    mode: "onChange",
    shouldFocusError: false,
    defaultValues,
  });
  const { handleSubmit, control, setValue, reset, trigger } = formMethods;
  const { metier, $type } = useWatch({ control });

  const filteredMetiers: Array<Metier> = useMemo(() => {
    const niveau = getMetierNiveauFromTypeContactMetier($type);
    return metiers.filter(
      (_metier) =>
        _metier.niveau === niveau && (competence == null || _metier.competence === competence)
    );
  }, [competence, metiers, $type]);

  useEffect(() => {
    if (
      ((competence != null && metier?.competence !== competence) ||
        metier?.niveau !== getMetierNiveauFromTypeContactMetier($type)) &&
      metier != null
    ) {
      setValue("metier", null);
    }
  }, [competence, metier, setValue, $type]);

  const [isSimilarContactsMetierDialogOpen, setIsSimilarContactsMetierDialogOpen] = useState(false);
  const [createContactMetierDto, setCreateContactMetierDto] = useState<CreateContactMetierDto>();
  const [similarContactsMetiers, setSimilarContactsMetiers] = useState<Array<ContactMetier>>([]);

  const { catchErrors, isLoading } = useSnackbarErrorHandler();
  async function createContactMetier(contactMetierDto: CreateContactMetierDto): Promise<void> {
    await catchErrors(async () => {
      await contactsMetiersService.create(contactMetierDto);

      enqueueSnackbar({
        variant: "success",
        autoHideDuration: 2000,
        message: ToastMessages.CONTACT_METIER_CREATION_SUCCESS,
      });
      setCompetence(undefined);
      reset({ ...defaultValues, $type: contactMetierDto.$type });
    });
  }

  const sendData: SubmitHandler<CreateContactMetierDto> = async function (
    createContactMetierDto: CreateContactMetierDto
  ): Promise<void> {
    await catchErrors(async () => {
      // On cherche s'il existe des contacts métiers similaires pour prévenir l'utilisateur
      const similarFilter: ContactMetierQueryFilter = {
        communesId:
          createContactMetierDto.commune?.id != null
            ? [createContactMetierDto.commune.id]
            : undefined,
        communesAssocieesDelegueesId:
          createContactMetierDto.cad?.id != null ? [createContactMetierDto.cad.id] : undefined,
        perimetresId:
          createContactMetierDto.perimetre?.id != null
            ? [createContactMetierDto.perimetre.id]
            : undefined,
        territoiresId:
          createContactMetierDto.territoire?.id != null
            ? [createContactMetierDto.territoire.id]
            : undefined,
        metiersCode:
          createContactMetierDto.metier?.code != null
            ? [createContactMetierDto.metier.code]
            : undefined,
      };

      const { values: _similarCms } = await contactsMetiersService.getByFilter(similarFilter);

      if (_similarCms.length > 0) {
        setIsSimilarContactsMetierDialogOpen(true);
        setCreateContactMetierDto(createContactMetierDto);
        setSimilarContactsMetiers(_similarCms);
      } else {
        await createContactMetier(createContactMetierDto);
      }
    });
  };

  function onDialogClose(): void {
    setIsSimilarContactsMetierDialogOpen(false);
    setCreateContactMetierDto(undefined);
    setSimilarContactsMetiers([]);
  }

  const setType = useCallback(
    (type: CreateContactMetierDto["$type"]): void => {
      setValue("$type", type);
      if (type !== "perimetre") setValue("perimetre", null);
      if (type !== "territoire") setValue("territoire", null);
      if (type !== "commune") setValue("commune", null);
      if (type !== "commune-associee-deleguee") setValue("cad", null);
      if (type !== "commune" && type !== "commune-associee-deleguee") setCompetence(undefined);

      requestAnimationFrame(() => {
        void trigger();
      });
    },
    [setValue, trigger]
  );

  return (
    <PageLayout pageTitle="Nouveau Contact Métier">
      <FormProvider {...formMethods}>
        <Grid
          component="form"
          container
          spacing={2}
          sx={{ mb: 2 }}
          size={12}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={handleSubmit(sendData, onFormErrorDefault)}>
          <Grid
            container
            size={12}
            rowSpacing={1}
            sx={{
              ".Mui-checked + .MuiFormControlLabel-label": {
                color: "primary.main",
                fontWeight: "500",
              },
            }}>
            <Typography variant="subtitle2" color="primary.dark" component={Grid} size={12}>
              Le nouveau Contact Métier concerne :
            </Typography>

            <Grid size={{ xs: 12, md: 6, xl: 3 }}>
              <FormControlLabel
                checked={$type === "commune"}
                onChange={(_) => {
                  setType("commune");
                }}
                value="commune"
                control={<Radio />}
                label="une Commune"
              />
              <ControlledCommuneAutoComplete
                disabled={$type !== "commune"}
                name="commune"
                label={`Commune${$type === "commune" ? " *" : ""}`}
                rules={{
                  validate: (value) =>
                    $type !== "commune" || value != null || "Veuillez sélectionner une Commune",
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6, xl: 3 }}>
              <FormControlLabel
                checked={$type === "commune-associee-deleguee"}
                onChange={(_) => {
                  setType("commune-associee-deleguee");
                }}
                value="cad"
                control={<Radio />}
                label="une Commune associée déléguée"
              />
              <ControlledCommuneAssocieeDelegueeAutoComplete
                disabled={$type !== "commune-associee-deleguee"}
                name="cad"
                label={`Commune associée déléguée${$type === "commune-associee-deleguee" ? " *" : ""}`}
                rules={{
                  validate: (value) =>
                    $type !== "commune-associee-deleguee" ||
                    value != null ||
                    "Veuillez sélectionner une Commune associée déléguée",
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6, xl: 3 }}>
              <FormControlLabel
                checked={$type === "perimetre"}
                onChange={(_) => {
                  setType("perimetre");
                }}
                value="perimetre"
                control={<Radio />}
                label="un Périmètre"
              />
              <ControlledPerimetreAutoComplete
                disabled={$type !== "perimetre"}
                name="perimetre"
                label={`Périmètre${$type === "perimetre" ? " *" : ""}`}
                rules={{
                  validate: (value) =>
                    $type !== "perimetre" || value != null || "Veuillez sélectionner un Périmètre",
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6, xl: 3 }}>
              <FormControlLabel
                checked={$type === "territoire"}
                onChange={(_) => {
                  setType("territoire");
                }}
                value="territoire"
                control={<Radio />}
                label="un Territoire"
              />
              <ControlledTerritoireAutoComplete
                disabled={$type !== "territoire"}
                name="territoire"
                label={`Territoire${$type === "territoire" ? " *" : ""}`}
                rules={{
                  validate: (value: Territoire | null) =>
                    $type !== "territoire" ||
                    value != null ||
                    "Veuillez sélectionner un Territoire",
                }}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle2" color="primary.dark" component={Grid} size={12}>
            Métier :
          </Typography>

          <Grid size={{ xs: 12, md: 4 }}>
            <CompetenceSelect
              label="Compétence"
              value={competence}
              onChange={setCompetence}
              disabled={$type !== "commune"}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 8 }}>
            {isMetiersLoading ? (
              <Skeleton height={56} />
            ) : (
              <ControlledMetierAutoComplete
                name="metier"
                label="Métier *"
                metiers={filteredMetiers}
                rules={{ required: "Veuillez sélectionner un Métier" }}
              />
            )}
          </Grid>

          <Typography variant="subtitle2" color="primary.dark" component={Grid} size={12}>
            Poste :
          </Typography>

          <Grid size={12}>
            {isPostesLoading ? (
              <Skeleton height={56} />
            ) : (
              <ControlledPosteAutoComplete
                name="poste"
                label="Poste *"
                postes={postes}
                rules={{ required: "Veuillez sélectionner un Poste" }}
              />
            )}
          </Grid>

          <Grid container size={12} sx={{ justifyContent: "flex-end" }}>
            <Button color="error" href="/">
              Annuler
            </Button>
            <Button
              variant="contained"
              type="submit"
              loading={isLoading}
              startIcon={<PersonAddAlt1Icon />}
              loadingPosition="start">
              Créer le Contact Métier
            </Button>
          </Grid>
        </Grid>
      </FormProvider>
      <SimilarCreationConfirmationDialog
        isOpen={isSimilarContactsMetierDialogOpen}
        onClose={onDialogClose}
        onConfirm={
          createContactMetierDto == null
            ? undefined
            : () => {
                void createContactMetier(createContactMetierDto);
              }
        }
        contactMetierToCreate={createContactMetierDto}
        similarContactsMetiers={similarContactsMetiers}
      />
    </PageLayout>
  );
}

export const NouveauContactMetierPageWithErrorBoundary =
  withPageErrorBoundary(NouveauContactMetierPage);
